import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Card, Col, Empty, Flex, Form, InputNumber, Row, Select, Space, Tag, Typography } from 'antd';
import { DownloadOutlined, ExperimentOutlined, ReloadOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import {
  avaliacaoValida, AvaliacaoPiloto, CasoPiloto, custoComprometido, exportarPiloto, lerMemoriaPiloto,
  MemoriaPiloto, memoriaVazia, MetricasVersao, PILOTO_STORAGE_KEY, podeIniciar, reconciliarCasos, Rotulo, TETO_USD,
} from './pilotoOpus';

const { Text, Paragraph } = Typography;
const usd = (n: number) => `US$ ${n.toFixed(4)}`;
const usdOpcional = (n: number | null) => n === null ? 'indisponível' : usd(n);
const segundosOpcionais = (n: number | null) => n === null ? 'indisponível' : `${Math.round(n / 1000)} s`;
interface Resultado {
  job_id: string; status: string; custo_reservado_usd?: number; custo_real_usd?: number;
  orcamento_acumulado_usd?: number; tokens?: unknown; latencia_ms?: unknown; reveal_token?: string;
  versoes?: { rotulo: Rotulo; conteudo: Record<string, unknown> }[];
  briefing?: unknown; avaliacao?: AvaliacaoPiloto; modelos_por_rotulo?: Record<Rotulo, string>; erro?: string;
  metricas_por_rotulo?: Record<Rotulo, MetricasVersao>;
}
async function api(body: Record<string, unknown>) {
  const token = await getToken();
  const res = await axios.post(`${API_URL}/agentes/exemplos`, body, { headers: { Authorization: token }, timeout: 25000 });
  const data = typeof res.data.body === 'string' ? JSON.parse(res.data.body) : res.data.body || res.data;
  if ((data.erro || data.error || data.ok === false) && data.status !== 'erro') throw new Error(data.erro || data.error || 'O piloto não concluiu esta operação.');
  return data;
}
function mensagemErro(e: unknown): string {
  if (axios.isAxiosError(e)) {
    if (e.response?.status === 403) return 'O piloto está disponível somente para administradores.';
    return 'Não foi possível confirmar a resposta. Consulte o andamento antes de iniciar outro caso.';
  }
  return e instanceof Error ? e.message : 'Não foi possível concluir a operação.';
}
function numero(n: unknown): n is number { return typeof n === 'number' && Number.isFinite(n) && n >= 0; }

export default function PilotoOpusTab() {
  const [casos, setCasos] = useState<CasoPiloto[]>([]);
  const [memoria, setMemoria] = useState<MemoriaPiloto>(memoriaVazia);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [erroLocal, setErroLocal] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const [casoId, setCasoId] = useState<string>();
  const [resultado, setResultado] = useState<Resultado>();
  const [form] = Form.useForm();
  const trava = useRef(false);
  const registro = memoria.registros.find(r => r.caso_id === casoId);

  const guardar = useCallback((atualizar: (m: MemoriaPiloto) => MemoriaPiloto) => {
    let anterior: MemoriaPiloto;
    try {
      anterior = lerMemoriaPiloto(localStorage.getItem(PILOTO_STORAGE_KEY));
    } catch (e) {
      setErroLocal('O registro local do orçamento não pôde ser conferido. Novas execuções estão bloqueadas.');
      throw e;
    }
    const m = atualizar(anterior);
    try {
      localStorage.setItem(PILOTO_STORAGE_KEY, JSON.stringify(m));
      setMemoria(m);
      return m;
    } catch (e) {
      setErroLocal('O navegador não conseguiu preservar o orçamento do piloto. Novas execuções estão bloqueadas.');
      throw e;
    }
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(''); setAviso('');
    try {
      const local = lerMemoriaPiloto(localStorage.getItem(PILOTO_STORAGE_KEY));
      setMemoria(local);
      const data = await api({ action: 'piloto_opus_listar' });
      if (!Array.isArray(data.casos) || data.casos.length > 10) throw new Error('Lista de casos inválida. Execução bloqueada.');
      setCasos(data.casos);
      setAviso(typeof data.aviso === 'string' ? data.aviso : '');
      const m = guardar(m => ({ ...m,
        acumulado_servidor_usd: numero(data.orcamento_acumulado_usd) ? data.orcamento_acumulado_usd : m.acumulado_servidor_usd,
        registros: reconciliarCasos(m, data.casos),
      }));
      setCasoId(id => data.casos.some((c: CasoPiloto) => c.caso_id === id)
        ? id
        : m.registros.find(r => data.casos.some((c: CasoPiloto) => c.caso_id === r.caso_id)
          && (r.status !== 'concluido' || !r.avaliacao))?.caso_id || data.casos[0]?.caso_id);
    } catch (e) { setErro(mensagemErro(e)); }
    finally { setCarregando(false); }
  }, [guardar]);

  useEffect(() => {
    carregar();
    const sincronizar = (e: StorageEvent) => {
      if (e.key !== PILOTO_STORAGE_KEY) return;
      try { setMemoria(lerMemoriaPiloto(e.newValue)); }
      catch { setErroLocal('O registro local foi alterado e não pôde ser conferido. Execução bloqueada.'); }
    };
    window.addEventListener('storage', sincronizar);
    return () => window.removeEventListener('storage', sincronizar);
  }, [carregar]);

  const jobId = registro?.job_id;
  const consultar = useCallback(async (id: string) => {
    const data: Resultado = await api({ action: 'piloto_opus_status', job_id: id });
    if (data.job_id !== id || !data.status) throw new Error('Resposta de andamento inválida.');
    guardar(m => ({ ...m,
      acumulado_servidor_usd: numero(data.orcamento_acumulado_usd) ? data.orcamento_acumulado_usd : m.acumulado_servidor_usd,
      registros: m.registros.map(r => r.job_id !== id ? r : { ...r, status: data.status,
        custo_reservado_usd: numero(data.custo_reservado_usd) ? data.custo_reservado_usd : r.custo_reservado_usd,
        custo_real_usd: numero(data.custo_real_usd) ? data.custo_real_usd : r.custo_real_usd,
        tokens: data.tokens, latencia_ms: data.latencia_ms,
        metricas_por_rotulo: data.metricas_por_rotulo || r.metricas_por_rotulo,
        avaliacao: data.avaliacao && avaliacaoValida(data.avaliacao) ? data.avaliacao : r.avaliacao,
        modelos_por_rotulo: data.modelos_por_rotulo || r.modelos_por_rotulo,
      }),
    }));
    setResultado(data);
    return data.status;
  }, [guardar]);

  useEffect(() => {
    setResultado(undefined); form.resetFields();
    if (!jobId) return;
    let cancelado = false;
    let timer: ReturnType<typeof setTimeout>;
    let falhasConsecutivas = 0;
    const poll = async () => {
      try {
        const status = await consultar(jobId);
        falhasConsecutivas = 0;
        if (!cancelado) setErro('');
        if (!cancelado && !['concluido', 'erro', 'falhou', 'falha'].includes(status)) timer = setTimeout(poll, 4000);
      } catch (e) {
        if (!cancelado) {
          falhasConsecutivas += 1;
          setErro(mensagemErro(e));
          timer = setTimeout(poll, Math.min(30_000, 4_000 * (2 ** Math.min(falhasConsecutivas, 3))));
        }
      }
    };
    poll();
    return () => { cancelado = true; clearTimeout(timer); };
  }, [jobId, consultar, form]);

  async function iniciar() {
    if (!casoId || trava.current || erroLocal) return;
    trava.current = true; setOcupado(true); setErro('');
    try {
      // Reserva local antes do envio: timeout/reload nunca dispara outra geração.
      guardar(m => {
        if (!podeIniciar(m, casoId)) throw new Error('Conclua o caso pendente ou confira o orçamento antes de continuar.');
        return { ...m, registros: [...m.registros, { caso_id: casoId, status: 'iniciando',
          iniciado_em: new Date().toISOString(), custo_reservado_usd: 0 }] };
      });
      const data = await api({ action: 'piloto_opus_iniciar', caso_id: casoId });
      if (!data.job_id || !numero(data.custo_reservado_usd)) throw new Error('Início sem confirmação completa. Use Atualizar casos para recuperar o andamento.');
      guardar(m => ({ ...m,
        acumulado_servidor_usd: numero(data.orcamento_acumulado_usd) ? data.orcamento_acumulado_usd : m.acumulado_servidor_usd,
        registros: m.registros.map(r => r.caso_id === casoId ? { ...r, job_id: data.job_id,
          status: data.status, custo_reservado_usd: data.custo_reservado_usd } : r),
      }));
    } catch (e) { setErro(mensagemErro(e)); }
    finally { setOcupado(false); trava.current = false; }
  }
  async function avaliar() {
    try {
      const valores = await form.validateFields();
      const avaliacao = { ...valores,
        A: { ...valores.A, erro_critico: valores.A.erro_critico === 'sim', aceitavel_primeira_revisao: valores.A.aceitavel_primeira_revisao === 'sim' },
        B: { ...valores.B, erro_critico: valores.B.erro_critico === 'sim', aceitavel_primeira_revisao: valores.B.aceitavel_primeira_revisao === 'sim' },
        avaliado_em: new Date().toISOString() };
      if (!avaliacaoValida(avaliacao) || !jobId || !resultado?.reveal_token) return;
      setOcupado(true); setErro('');
      const data = await api({
        action: 'piloto_opus_revelar', job_id: jobId,
        reveal_token: resultado.reveal_token, avaliacao,
      });
      if (!data.modelos_por_rotulo?.A || !data.modelos_por_rotulo?.B) {
        throw new Error('A avaliação foi enviada, mas a identificação dos modelos não foi confirmada. Consulte o andamento.');
      }
      guardar(m => ({ ...m, registros: m.registros.map(r => r.job_id === jobId
        ? { ...r, avaliacao: data.avaliacao && avaliacaoValida(data.avaliacao) ? data.avaliacao : avaliacao,
          modelos_por_rotulo: data.modelos_por_rotulo,
          metricas_por_rotulo: data.metricas_por_rotulo }
        : r) }));
      setResultado(atual => atual?.job_id === jobId
        ? { ...atual, avaliacao: data.avaliacao || avaliacao,
          modelos_por_rotulo: data.modelos_por_rotulo, metricas_por_rotulo: data.metricas_por_rotulo }
        : atual);
    } catch (e) { if (!(e as { errorFields?: unknown }).errorFields) setErro(mensagemErro(e)); }
    finally { setOcupado(false); }
  }
  function baixar() {
    const url = URL.createObjectURL(new Blob([JSON.stringify(exportarPiloto(memoria), null, 2)], { type: 'application/json' }));
    const a = document.createElement('a'); a.href = url; a.download = 'dwight-piloto-opus-avaliacao.json'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  const versoesValidas = resultado?.job_id === jobId && resultado?.status === 'concluido' && resultado.versoes?.length === 2
    && ['A', 'B'].every(l => resultado.versoes?.some(v => v.rotulo === l && v.conteudo && typeof v.conteudo === 'object'));
  const comprometido = custoComprometido(memoria);
  const erroResultado = resultado?.job_id === jobId ? resultado?.erro : undefined;

  return <Space direction="vertical" size={16} style={{ width: '100%' }}>
    <Alert type="info" showIcon message="Piloto de redação e revisão do escopo — 10 casos"
      description="Sonnet continua como padrão do Dwight. O piloto gera duas versões para comparação, sem alterar propostas ou composições. Pode apontar lacunas que afetam a composição; horas, quantidades, custos, markup e preços continuam nas ferramentas de cálculo. Apenas andamento, custos e avaliações ficam neste navegador; briefing e textos gerados não ficam no histórico local nem no arquivo de resultados." />
    <Flex gap={12} wrap align="center">
      <Tag color={comprometido >= TETO_USD ? 'red' : 'blue'}>Orçamento comprometido: {usd(comprometido)} / US$ 15</Tag>
      <Text>Custo por uso informado: {usd(memoria.registros.reduce((s, r) => s + (r.custo_real_usd ?? 0), 0))}</Text>
      <Text>{memoria.registros.filter(r => r.avaliacao).length}/10 avaliados</Text>
      <Button icon={<DownloadOutlined />} disabled={!memoria.registros.length} onClick={baixar}>Baixar resultados JSON</Button>
    </Flex>
    <Text type="secondary">O valor por uso vem dos tokens retornados pelo serviço; a fatura pode incluir ajustes. A reserva de execuções pendentes também ocupa o teto. Use este navegador até concluir e baixar os resultados.</Text>
    {erroLocal && <Alert type="error" showIcon message={erroLocal} />}
    {erro && <Alert type="error" showIcon message={erro} />}
    {aviso && <Alert type="warning" showIcon message={aviso} />}
    {comprometido >= TETO_USD && <Alert type="warning" showIcon message="Teto de US$ 15 atingido. Novas execuções estão bloqueadas; você ainda pode avaliar e baixar resultados." />}
    <Flex gap={8} wrap>
      <Select aria-label="Caso do piloto" value={casoId} onChange={setCasoId} loading={carregando} disabled={ocupado}
        style={{ flex: '1 1 280px', minWidth: 0 }} placeholder="Selecione um caso"
        options={casos.map(c => ({ value: c.caso_id, label: `${c.codigo} · ${c.tipo}${c.area ? ` · ${c.area}` : ''}${c.tem_revisao ? ' · com revisão' : ''}` }))} />
      <Button icon={<ReloadOutlined />} loading={carregando} disabled={ocupado} onClick={carregar}>Atualizar casos</Button>
      <Button type="primary" icon={<ExperimentOutlined />} loading={ocupado} onClick={iniciar}
        disabled={carregando || !!erroLocal || !casoId || !podeIniciar(memoria, casoId)}>Executar este caso</Button>
    </Flex>
    {!carregando && casos.length === 0 && <Empty description="Nenhum caso disponível para o piloto." />}
    {registro && <Card size="small" title={`Andamento: ${registro.status}`}>
      <Space wrap>
        <Text>Reserva: {usd(registro.custo_reservado_usd)}</Text>
        <Text>Custo do caso: {registro.custo_real_usd === undefined ? 'aguardando confirmação' : usd(registro.custo_real_usd)}</Text>
        {jobId && <Button size="small" onClick={() => consultar(jobId).catch(e => setErro(mensagemErro(e)))}>Consultar andamento</Button>}
      </Space>
      {!jobId && <Paragraph>O início ainda não foi confirmado. Atualize os casos para recuperar a execução; não repita o envio.</Paragraph>}
      {registro.status === 'erro' && <Alert style={{ marginTop: 12 }} type="error" showIcon
        message={erroResultado
          || 'A execução deste caso falhou. A reserva continua no orçamento enquanto o custo não estiver confirmado. O caso não será executado novamente.'} />}
    </Card>}
    {resultado?.job_id === jobId && resultado?.status === 'concluido' && !versoesValidas && <Alert type="error" showIcon message="O caso terminou sem duas versões válidas para avaliação. Consulte o andamento; não gere novamente." />}
    {versoesValidas && <>
      <Card title="Informações de referência do caso" size="small">
        <Paragraph style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{resultado?.briefing
          ? typeof resultado.briefing === 'string' ? resultado.briefing : JSON.stringify(resultado.briefing, null, 2)
          : 'O serviço não forneceu o briefing. Confira as fontes antes de registrar a avaliação.'}</Paragraph>
      </Card>
      <Row gutter={[16, 16]}>{(['A', 'B'] as Rotulo[]).map(rotulo => <Col key={rotulo} xs={24} lg={12}>
        <Card title={`Versão ${rotulo}`} style={{ height: '100%' }}>
          {registro?.modelos_por_rotulo && <Tag color="blue">{registro.modelos_por_rotulo[rotulo]}</Tag>}
          {registro?.avaliacao && resultado?.metricas_por_rotulo?.[rotulo] && <Space wrap style={{ marginBottom: 12 }}>
            <Text type="secondary">Entrada: {resultado.metricas_por_rotulo[rotulo].tokens_in ?? 'indisponível'} tokens</Text>
            <Text type="secondary">Saída: {resultado.metricas_por_rotulo[rotulo].tokens_out ?? 'indisponível'} tokens</Text>
            <Text type="secondary">Custo: {usdOpcional(resultado.metricas_por_rotulo[rotulo].custo_real_usd)}</Text>
            <Text type="secondary">Latência: {segundosOpcionais(resultado.metricas_por_rotulo[rotulo].latencia_ms)}</Text>
          </Space>}
          {Object.entries(resultado!.versoes!.find(v => v.rotulo === rotulo)!.conteudo).map(([campo, valor]) => <div key={campo} style={{ marginBottom: 16 }}>
            <Text strong>{campo.replace(/_/g, ' ')}</Text>
            <Paragraph style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', marginTop: 4 }}>{typeof valor === 'string' ? valor : JSON.stringify(valor, null, 2)}</Paragraph>
          </div>)}
        </Card>
      </Col>)}</Row>
      {registro?.avaliacao ? <Card title="Avaliação registrada">
        <Paragraph>Preferência: {registro.avaliacao.vencedor}. A avaliação foi fixada antes da identificação dos modelos.</Paragraph>
        {registro.modelos_por_rotulo ? <Text>A: {registro.modelos_por_rotulo.A} · B: {registro.modelos_por_rotulo.B}</Text>
          : <Text type="secondary">Consulte o andamento para recuperar a identificação dos modelos.</Text>}
      </Card> : <Card title="Avalie antes de revelar os modelos">
        <Paragraph>Notas de 1 (insuficiente) a 5 (pronto para revisão final). Confira as fontes do caso e marque qualquer erro que impeça usar a proposta.</Paragraph>
        <Form form={form} layout="vertical">
          <Row gutter={[24, 12]}>{(['A', 'B'] as Rotulo[]).map(l => <Col xs={24} lg={12} key={l}>
            <Text strong>Versão {l}</Text>
            {Object.entries({ fidelidade: 'Fidelidade às fontes', completude: 'Cobertura do escopo', coerencia: 'Coerência técnica/comercial', clareza: 'Clareza da redação' }).map(([k, label]) =>
              <Form.Item key={k} name={[l, k]} label={label} rules={[{ required: true, message: 'Escolha uma nota.' }]}>
                <Select options={[1, 2, 3, 4, 5].map(value => ({ value, label: String(value) }))} />
              </Form.Item>)}
            <Form.Item name={[l, 'erro_critico']} label="Há erro crítico ou informação inventada?" rules={[{ required: true, message: 'Confira este critério.' }]}>
              <Select options={[{ value: 'sim', label: 'Sim' }, { value: 'nao', label: 'Não' }]}
                getPopupContainer={trigger => trigger.parentElement!} />
            </Form.Item>
            <Form.Item name={[l, 'aceitavel_primeira_revisao']} label="Aceitável já na primeira revisão?" rules={[{ required: true, message: 'Registre o aceite desta versão.' }]}>
              <Select options={[{ value: 'sim', label: 'Sim' }, { value: 'nao', label: 'Não' }]} />
            </Form.Item>
            <Form.Item name={[l, 'minutos_revisao']} label="Minutos estimados de correção humana" rules={[{ required: true, message: 'Informe o tempo.' }]}>
              <InputNumber min={0} max={10000} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>)}</Row>
          <Form.Item name="vencedor" label="Qual versão você escolheria?" rules={[{ required: true, message: 'Registre sua preferência.' }]}>
            <Select options={[{ value: 'A', label: 'Versão A' }, { value: 'B', label: 'Versão B' }, { value: 'empate', label: 'Empate' }, { value: 'nenhuma', label: 'Nenhuma é aceitável' }]} />
          </Form.Item>
          <Form.Item name="motivo" label="Principal motivo" rules={[{ required: true, message: 'Selecione o motivo.' }]}>
            <Select options={['Fidelidade às fontes', 'Escopo mais completo', 'Coerência técnica/comercial', 'Redação mais clara', 'Menor retrabalho', 'Qualidade equivalente', 'Erros críticos nas duas'].map(value => ({ value, label: value }))} />
          </Form.Item>
          <Button type="primary" onClick={avaliar} loading={ocupado} disabled={!resultado?.reveal_token}>
            Registrar avaliação e revelar modelos
          </Button>
        </Form>
      </Card>}
    </>}
  </Space>;
}
